import {
  Luach,
  measureTimeColumn,
  paginateYearTable,
  YearTable,
} from '@app/luach'
import { DEFAULT_CALENDAR_SPELLING, spelled } from '@app/shared'
import { plain } from '@app/stationery'
import '@app/stationery/stationery.css'
import '@app/luach/luach.css'
import '@app/luach/classification.css'
import '@app/luach/conditions.css'
import '@app/luach/yeartable.css'
// The page rules a BROWSER printing this screen needs — the sheet reaches the
// paper through the renderer, but Ctrl-P on the live page still has to produce
// the sheet and not the app around it.
import '@app/luach/print.css'
import { useMutation, useQuery } from '@tanstack/react-query'
import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { CURRENT_HEBREW_YEAR, YEARS } from '../lib/hebrew-year'
import type { OrgListItem } from '../lib/org'
import { pdfUrl, yearTableUrl } from '../lib/pdf'
import { usePersistentState } from '../lib/persist'
import { useToday } from '../lib/today'
import { useTRPC } from '../lib/trpc'
import { Notice } from '../ui'
import { SheetProof } from '../ui/SheetProof'
import { useDebounced } from '../ui/useDebounced'
import { ScheduleToolbar } from './ScheduleToolbar'
import {
  ALL_SHABBOSIM,
  COLUMN_GROUPS,
  CUSTOM,
  DEFAULTS,
  orientationFor,
} from './schedule-sheet'

/**
 * The schedule, as the office works it.
 *
 * The public page reads the same `occasion.resolve` result in a layout of its
 * own (`PublicSchedule`); neither computes a time, so the two cannot show
 * different schedules. This screen carries what only the office needs: the
 * fall-pattern picker, the letterhead, the year sheet's columns and the logic
 * view with each line's rationale.
 *
 * One field asks which sheet of times you want, and every answer lives in it:
 * a Shabbos, a chag, or every Shabbos of the year. The year is a sheet like the
 * others — the same shul, the same rules, the same engine — so a tab beside
 * this screen would have made it a different subject, and the second field
 * would have had to say which of two field sets it was showing anyway.
 */
export function ScheduleScreen({
  spaceId,
  shul,
}: {
  spaceId: string | null
  shul: OrgListItem | undefined
}) {
  const trpc = useTRPC()
  const spelling = shul?.calendarSpelling ?? DEFAULT_CALENDAR_SPELLING
  // Persisted across tab switches so leaving and returning to Schedule keeps the
  // chosen occasion and date rather than resetting.
  const [occasionKey, setOccasionKey] = usePersistentState(
    'sched.occasion',
    'shabbos',
  )
  const today = useToday(shul?.timeZoneId)
  // Null until the calendar lands on a real occurrence, so the starting point is
  // today at the shul rather than whichever zone was known at mount.
  const [chosenFrom, setFrom] = usePersistentState<string | null>(
    'sched.from',
    null,
  )
  const from = chosenFrom ?? today
  // Whether the "open on the next upcoming occasion" default has run this SPA
  // session — persisted so returning to the tab keeps the user's choice.
  const [picked, setPicked] = usePersistentState('sched.picked', false)
  // Which set of columns the year sheet carries: a shipped one by name, or the
  // columns chosen by hand.
  const [sheetKey, setSheetKey] = usePersistentState('sched.sheet', 'shabbosim')
  const [customColumns, setCustomColumns] = usePersistentState<string[]>(
    'sched.columns',
    [],
  )
  const [working, setWorking] = useState(false)
  // On by default: the reasoning sheet's whole job is that a guess does not read
  // as settled, so this is turned OFF for a reference copy, never on for review.
  const [showConfidence, setShowConfidence] = useState(true)
  // Print preview: the rendered document itself, in place of the live sheet.
  const [preview, setPreview] = useState(false)
  // Sheet content options. Each applies to the screen AND the PDF — one sheet,
  // one set of options.
  const [showSubtitle, setShowSubtitle] = useState(DEFAULTS.showSubtitle)
  const [showCivilDate, setShowCivilDate] = useState(DEFAULTS.showCivilDate)
  /*
   * The letterhead is the SHUL's, not this browser's — every sheet the
   * congregation prints carries the same one, so it is read from the org and
   * written back to it. Held in local state as well so the sheet redraws as you
   * choose rather than waiting on a round trip to show you what you picked.
   */
  const [letterhead, setLetterhead] = useState(shul?.letterhead ?? 'plain')
  /* The org arrives after the first render, and switching org must bring its
     own stationery with it. Keyed on the space so a later edit is not undone by
     a refetch of the value it just replaced. */
  const stationeryFor = useRef<string | null>(null)
  useEffect(() => {
    if (!shul || stationeryFor.current === shul.spaceId) return
    stationeryFor.current = shul.spaceId
    setLetterhead(shul.letterhead)
  }, [shul])

  const saveStationery = useMutation(trpc.org.update.mutationOptions())
  const chooseLetterhead = (next: string) => {
    setLetterhead(next)
    if (!shul) return
    saveStationery.mutate({ spaceId: shul.spaceId, letterhead: next })
  }
  // The second toolbar row. Collapsed by default — the page is for reading the
  // sheet, not for its controls.
  // Browsing by Hebrew year is how you reach configurations that only occur in
  // some years — a Tisha b'Av that falls on Sunday, for instance.
  const [hebrewYear, setHebrewYear] = usePersistentState(
    'sched.year',
    CURRENT_HEBREW_YEAR,
  )
  // When a step runs off the current year's list, which end of the adjacent
  // year's list to land on once it loads.
  const [edge, setEdge] = useState<'first' | 'last' | null>(null)

  // Every Shabbos of the year at once, rather than one sheet. The pickers, the
  // stepper and the sheet options all read this: a year has no date to step to
  // and no single occasion to resolve.
  const isYear = occasionKey === ALL_SHABBOSIM

  const occasions = useQuery({
    ...trpc.occasion.list.queryOptions({ spaceId: spaceId ?? '' }),
    enabled: !!spaceId,
  })

  const occurrences = useQuery({
    ...trpc.occasion.occurrences.queryOptions({
      spaceId: spaceId ?? '',
      occasionKey,
      // Start a little before today so the current week is always offered.
      hebrewYear,
      count: 120,
    }),
    enabled: !!spaceId && !isYear,
  })

  const schedule = useQuery({
    ...trpc.occasion.resolve.queryOptions({
      spaceId: spaceId ?? '',
      occasionKey,
      from,
    }),
    enabled: !!spaceId && !isYear,
  })

  /* What a year sheet can be made of — the columns this space can print and the
     sets that ship. Both from the server, which owns them: a copy kept here
     would be a list that disagrees about what "Candle lighting" prints. */
  const shapes = useQuery({
    ...trpc.occasion.yearShapes.queryOptions({ spaceId: spaceId ?? '' }),
    enabled: !!spaceId && isYear,
  })
  const presets = shapes.data?.presets ?? []
  const vocabulary = shapes.data?.columns ?? []

  const isCustom = sheetKey === CUSTOM
  const sheetPreset = presets.find((p) => p.key === sheetKey)
  const sheetColumns = isCustom ? customColumns : (sheetPreset?.columns ?? [])
  const orientation = isCustom
    ? orientationFor(sheetColumns)
    : (sheetPreset?.orientation ?? 'landscape')

  const year = useQuery({
    ...trpc.occasion.year.queryOptions({
      spaceId: spaceId ?? '',
      hebrewYear,
      // A shipped set travels by NAME so the server stays the authority on what
      // it means; a set built here travels as the columns it is.
      ...(isCustom ? { columns: customColumns } : { preset: sheetKey }),
    }),
    enabled: !!spaceId && isYear && (!isCustom || customColumns.length > 0),
  })

  /*
   * Choosing a column makes the sheet yours, whichever set you started from —
   * the alternative is a shipped set silently meaning something else.
   *
   * The set it started from keeps its order and a new column joins the end of
   * its own day. The vocabulary's order is the order the shul's lines were
   * found in, which is not the order a sheet reads left to right — so ordering
   * the whole set by it would rearrange a shipped set the moment somebody added
   * one column to it.
   */
  /* Where each column sits in the day, from the built year — the picker needs
     it for a column the sheet does not carry yet, which is why it covers the
     whole vocabulary rather than the columns on screen. */
  const order = year.data?.ordering ?? {}

  const inGroup = (
    c: { role: string | null; family: string },
    g: (typeof COLUMN_GROUPS)[number],
  ) => c.role === g.role && (c.family === 'zman') === g.zmanim

  const chooseColumns = (next: string[]) => {
    const wanted = new Set(next)
    const byId = new Map(vocabulary.map((c) => [c.id, c]))
    /*
     * When a column lands, from the built year. A fact leads its day — it says
     * which day the row is, not what happens during it — and anything the year
     * has no time for trails rather than jumping the queue.
     */
    const at = (id: string) =>
      order[id] ??
      (byId.get(id)?.family === 'fact'
        ? Number.NEGATIVE_INFINITY
        : Number.POSITIVE_INFINITY)

    setCustomColumns(
      COLUMN_GROUPS.flatMap((g) => {
        const kept = sheetColumns.filter((id) => {
          const c = byId.get(id)
          return wanted.has(id) && !!c && inGroup(c, g)
        })
        const added = vocabulary
          .filter(
            (c) => inGroup(c, g) && wanted.has(c.id) && !kept.includes(c.id),
          )
          .map((c) => c.id)
          .sort((a, b) => at(a) - at(b))

        /* The set you had keeps its order; a new column goes where the clock
           puts it, not on the end. A sheet is read across, so a shiur at 8:15
           belongs beside shachris — appended, it reads as an afterthought and
           the eye has to travel the row to find it. */
        const out = [...kept]
        for (const id of added) {
          const i = out.findIndex((held) => at(held) > at(id))
          out.splice(i < 0 ? out.length : i, 0, id)
        }
        return out
      }),
    )
    setSheetKey(CUSTOM)
  }

  /* Custom opens on the set you were looking at, so it is an edit of a sheet
     rather than a blank page to rebuild from nothing. */
  const chooseSheet = (next: string) => {
    if (next === CUSTOM && customColumns.length === 0) {
      setCustomColumns(sheetColumns)
    }
    setSheetKey(next)
  }

  // The whole calendar of the year in one chronological list — every Shabbos and
  // chag interleaved. The arrows walk THIS (the next thing on the calendar,
  // across occasions), not the next occurrence of the selected occasion.
  const timeline = useQuery({
    ...trpc.occasion.timeline.queryOptions({
      spaceId: spaceId ?? '',
      hebrewYear,
    }),
    enabled: !!spaceId,
  })
  const tl = timeline.data ?? []

  const isWeekly = occasionKey === 'shabbos'

  // Every way this chag can fall, from the engine — which scans until it has
  // seen every kevia, so the picker offers all of them and not the ones that
  // happen to fall inside a window.
  const patterns = useQuery({
    ...trpc.occasion.fallPatterns.queryOptions({ spaceId: spaceId ?? '' }),
    enabled: !!spaceId && !isWeekly && !isYear,
  })

  // The fall-pattern picker: one option per way the chag can fall. Keyed on the
  // pattern, not the weekday — a fast pushed off Shabbos and a fast that falls on
  // Sunday of its own accord both start Sunday and are different days.
  const fallPatterns =
    patterns.data?.find((p) => p.occasionKey === occasionKey)?.patterns ?? []
  const patternKey = (p: { weekday: string; flagKeys: string[] }) =>
    `${p.weekday}|${p.flagKeys.join(',')}`
  // The soonest occurrence still ahead, so picking a shape lands on a sheet
  // worth printing; a pattern whose every listed year has passed falls back to
  // what it has, which the scan's own span makes vanishingly rare.
  const nextStart = (p: { startDates: string[] }) =>
    p.startDates.find((d) => d >= today) ?? p.startDates[0]
  const selectedPattern = fallPatterns.find((p) => p.startDates.includes(from))

  const list = occurrences.data ?? []
  // Which option is showing: the last occurrence at or before `from`.
  const selectedStart =
    list.find((o) => o.startDate >= from)?.startDate ?? list[0]?.startDate

  // Where we sit in the calendar. The arrows step to the adjacent entry — which
  // may be a different occasion — and roll across the year edge so there is never
  // a dead end.
  const tlIndex = tl.findIndex(
    (t) => t.occasionKey === occasionKey && t.startDate === from,
  )
  const step = (delta: number) => {
    const target = tl[tlIndex + delta]
    if (target) {
      setOccasionKey(target.occasionKey)
      setFrom(target.startDate)
      return
    }
    setHebrewYear((y) => y + delta)
    setEdge(delta < 0 ? 'last' : 'first')
  }

  // After a year change from an arrow, land on that year's first/last calendar
  // item (which occasion, and its date).
  useEffect(() => {
    if (!edge || tl.length === 0) return
    const t = edge === 'last' ? tl[tl.length - 1] : tl[0]
    setOccasionKey(t.occasionKey)
    setFrom(t.startDate)
    setEdge(null)
  }, [tl, edge, setOccasionKey, setFrom])

  // On first open this SPA session, jump to the next upcoming occasion on the
  // calendar (across occasions) rather than the first Shabbos of the year.
  // The year sheet is a choice, not a date, so it is never landed on or away
  // from: it stands outside the calendar this walks.
  useEffect(() => {
    if (picked || isYear || tl.length === 0) return
    const next = tl.find((t) => t.startDate >= from) ?? tl[tl.length - 1]
    setOccasionKey(next.occasionKey)
    setFrom(next.startDate)
    setPicked(true)
  }, [tl, from, picked, isYear, setOccasionKey, setFrom, setPicked])

  // Keep `from` on a real occurrence of the selected occasion after the occasion
  // or year changes from the pickers — snapping to the next one at/after the
  // current date, not back to the start of the year.
  //
  // It stands down while another effect above is mid-landing, the way it already
  // does before the first pick. A landing sets the occasion and the date
  // together, and until both have been committed `list` still belongs to the
  // occasion being left — against which the incoming date looks invalid, so the
  // snap would drag the arrow back to that occasion's first occurrence.
  useEffect(() => {
    if (!picked || edge) return
    if (list.length > 0 && !list.some((o) => o.startDate === from)) {
      setFrom((list.find((o) => o.startDate >= from) ?? list[0]).startDate)
    }
  }, [list, from, picked, edge, setFrom])

  // Always include the shown year, so paging past the preset range still selects
  // a valid option rather than blanking the picker.
  const yearOptions = [...new Set([...YEARS, hebrewYear])].sort((a, b) => a - b)

  /* The year of Shabbosim sits directly under Shabbos: it is the same subject
     at a year's grain, and the two are read together. A space with no weekly
     occasion to sit under still offers it, at the end — an option nobody can
     find is worse than one in an unexpected place. */
  const yearOption = {
    value: ALL_SHABBOSIM,
    label: spelled('All Shabbosim', spelling),
    hint: 'כל השבתות',
  }
  const occasionOptions = (occasions.data ?? []).flatMap((o) => {
    const option = { value: o.key, label: o.labelEn, hint: plain(o.labelHe) }
    return o.key === 'shabbos' ? [option, yearOption] : [option]
  })
  if (!occasionOptions.some((o) => o.value === ALL_SHABBOSIM)) {
    occasionOptions.push(yearOption)
  }

  /*
   * The sheet as a document, addressed once. Printing and downloading are the
   * same artifact going to two places, so they ask the same question — a second
   * expression of it is how the two come to disagree about which sheet is on
   * screen. Logic already says which one is being looked at, so it says which
   * one comes out, rather than asking again.
   */
  const sheetUrl = (space: string) =>
    isYear
      ? yearTableUrl({
          spaceId: space,
          year: hebrewYear,
          preset: isCustom ? undefined : sheetKey,
          columns: isCustom ? customColumns : undefined,
          orientation,
          showSubtitle,
        })
      : pdfUrl({
          spaceId: space,
          occasionKey,
          from,
          showSubtitle,
          showCivilDate,
          letterhead,
          reasoning: working,
          showConfidence,
        })
  const sheetReady = !!spaceId && !!(isYear ? year.data : schedule.data)
  /* The address once it has stopped moving. Printing and downloading are single
     acts and take it live; the preview watches it, and a render is a headless
     browser laying out a page — so what it watches is where the options came to
     rest, not every value they passed through on the way. */
  const settledSheet = useDebounced(spaceId ? sheetUrl(spaceId) : '', 500)

  return (
    <>
      <ScheduleToolbar
        hebrewYear={hebrewYear}
        setHebrewYear={setHebrewYear}
        yearOptions={yearOptions}
        spelling={spelling}
        occasionKey={occasionKey}
        setOccasionKey={setOccasionKey}
        occasionOptions={occasionOptions}
        isYear={isYear}
        sheetKey={sheetKey}
        chooseSheet={chooseSheet}
        presets={presets}
        isWeekly={isWeekly}
        fallPatterns={fallPatterns}
        selectedPattern={selectedPattern}
        patternKey={patternKey}
        nextStart={nextStart}
        setFrom={setFrom}
        selectedStart={selectedStart}
        occurrences={occurrences}
        step={step}
        sheetReady={sheetReady}
        spaceId={spaceId}
        sheetUrl={sheetUrl}
        showSubtitle={showSubtitle}
        setShowSubtitle={setShowSubtitle}
        vocabulary={vocabulary}
        inGroup={inGroup}
        sheetColumns={sheetColumns}
        chooseColumns={chooseColumns}
        showCivilDate={showCivilDate}
        setShowCivilDate={setShowCivilDate}
        preview={preview}
        setPreview={setPreview}
        working={working}
        setWorking={setWorking}
        showConfidence={showConfidence}
        setShowConfidence={setShowConfidence}
        letterhead={letterhead}
        chooseLetterhead={chooseLetterhead}
      />

      {isYear ? (
        <>
          {year.isLoading && <Notice>Building the year…</Notice>}
          {year.error && <Notice tone="error">{year.error.message}</Notice>}
          {isCustom && customColumns.length === 0 && (
            <Notice>Choose at least one column.</Notice>
          )}
          {year.data && shul && (
            <YearTableContainer
              // Remount when the sheet changes so the pages are laid out against
              // the new content rather than keeping the previous one's.
              key={`${hebrewYear}-${sheetKey}-${sheetColumns.join(',')}-${showSubtitle}`}
            >
              <YearTable
                shul={shul}
                titleHe={`לוח זמנים ${year.data.hebrewYearHe}`}
                columns={year.data.columns}
                rows={year.data.rows}
                orientation={orientation}
                showSubtitle={showSubtitle}
                print
              />
            </YearTableContainer>
          )}
        </>
      ) : null}

      {!isYear && schedule.isLoading && (
        <Notice>Computing the schedule…</Notice>
      )}
      {!isYear && schedule.error && (
        <Notice tone="error">{schedule.error.message}</Notice>
      )}

      {/* Previewing shows the DOCUMENT, not a drawing of it: the same PDF the
      printer is handed, rendered by the same service, so there is nothing left
      that could quietly differ. The live sheet is what the rest of the time
      wants — instant, and carrying the reasoning a page cannot hold. */}
      {!isYear && preview && sheetReady && spaceId && (
        /* Keyed on the document, so changing an option is a new sheet arriving
           rather than the old one standing there looking current. Off the
           SETTLED address, so walking the calendar or trying the letterheads
           one after another is one render at the end rather than a render each
           — every one of them is a headless browser laying out a page. */
        <SheetProof key={settledSheet} url={settledSheet} flowing={working} />
      )}

      {!isYear && !preview && schedule.data && shul && (
        <LuachContainer
          // Remount on a new schedule so the time column is measured against the
          // new content rather than keeping the previous sheet's.
          key={`${occasionKey}-${from}-${showSubtitle}-${showCivilDate}-${letterhead}`}
        >
          <Luach
            result={schedule.data}
            shul={shul}
            /* The live sheet reads the same options the document does, so
               choosing one shows what it does before the page is fetched. What
               it does not do is lay itself out as a PAGE: how the days column
               and where they break is the document's answer, and the document
               is what the preview shows. */
            options={{
              showSubtitle,
              showCivilDate,
              letterhead,
              columns: 1,
              working,
              showConfidence,
            }}
          />
        </LuachContainer>
      )}
    </>
  )
}

/**
 * The year sheet's preview frame.
 *
 * It runs the SAME paginator the PDF renderer injects, so the breaks on screen
 * are the breaks on paper — a preview that laid the pages out its own way would
 * be the thing this stack refuses.
 *
 * The sheet keeps true Letter geometry and the frame scrolls. It is deliberately
 * NOT scaled to fit a narrow window: a preview scaled to a phone lies about the
 * size of what comes out of the printer.
 */
export function YearTableContainer({
  children,
}: {
  children: React.ReactNode
}) {
  const ref = useRef<HTMLDivElement>(null)

  useLayoutEffect(() => {
    const root = ref.current?.querySelector<HTMLElement>('.ytable')
    if (!root) return
    let cancelled = false
    // Fonts decode asynchronously and every column width here is measured from
    // text — measured in a fallback face, every column is the wrong size.
    void document.fonts.ready.then(() => {
      if (cancelled) return
      paginateYearTable(root)
    })
    return () => {
      cancelled = true
    }
  }, [])

  return (
    <div className="ytable-frame" ref={ref}>
      {children}
    </div>
  )
}

/**
 * Holds the live sheet: a card in the app. The container is deliberately
 * OUTSIDE the luach component — it is the one thing that legitimately differs
 * between screen and paper, so keeping it here lets the sheet itself stay
 * identical in both.
 *
 * It does not answer what a PAGE looks like. `SheetProof` does, by fetching one.
 */
function LuachContainer({ children }: { children: React.ReactNode }) {
  const ref = useRef<HTMLDivElement>(null)

  useLayoutEffect(() => {
    const root = ref.current?.querySelector<HTMLElement>('.luach')
    if (!root) return
    // Size the time column on screen the same way the print pass does, so a
    // wide row (the composite shema times) never overflows the fixed default.
    measureTimeColumn(root)
  }, [])

  return (
    <div className="luach-frame" ref={ref}>
      {children}
    </div>
  )
}
